import * as THREE from 'three'
import { GLTF, SkeletonUtils } from 'three-stdlib'
import { useAnimations, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { button, useControls } from "leva";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGraph } from '@react-three/fiber'
import { useChat } from "../hooks/useChat";
import { AnimationClip, Group } from 'three';
import { transcribeBase64AudioToVisemes } from '../../../../../utils/PocketSphinx';
import { AppDispatch } from '../../../../../store';
import { setPlaying } from '../CandidateInterviewBotSlice';

type GLTFResult = GLTF & {
  nodes: {
    Wolf3D_Hair: THREE.SkinnedMesh
    Wolf3D_Body: THREE.SkinnedMesh
    Wolf3D_Outfit_Bottom: THREE.SkinnedMesh
    Wolf3D_Outfit_Footwear: THREE.SkinnedMesh
    Wolf3D_Outfit_Top: THREE.SkinnedMesh
    EyeLeft: THREE.SkinnedMesh
    EyeRight: THREE.SkinnedMesh
    Wolf3D_Head: THREE.SkinnedMesh
    Wolf3D_Teeth: THREE.SkinnedMesh
    Hips: THREE.Bone
  }
  materials: {
    Wolf3D_Hair: THREE.MeshStandardMaterial
    Wolf3D_Body: THREE.MeshStandardMaterial
    Wolf3D_Outfit_Bottom: THREE.MeshStandardMaterial
    Wolf3D_Outfit_Footwear: THREE.MeshStandardMaterial
    Wolf3D_Outfit_Top: THREE.MeshStandardMaterial
    Wolf3D_Eye: THREE.MeshStandardMaterial
    Wolf3D_Skin: THREE.MeshStandardMaterial
    Wolf3D_Teeth: THREE.MeshStandardMaterial
  }
}

const genderMapping: any = {
  'Michael': 'male',
  'Lynn': 'female',
  'Janelle': 'female'
}

const facialExpressions: any = {
  default: {},
  smile: {
    browInnerUp: 0.17,
    eyeSquintLeft: 0.4,
    eyeSquintRight: 0.44,
    noseSneerLeft: 0.1700000727403593,
    noseSneerRight: 0.14000002836874015,
    mouthPressLeft: 0.61,
    mouthPressRight: 0.41000000000000003,
  },
  funnyFace: {
    jawLeft: 0.63,
    mouthPucker: 0.53,
    noseSneerLeft: 1,
    noseSneerRight: 0.39,
    mouthLeft: 1,
    eyeLookUpLeft: 1,
    eyeLookUpRight: 1,
    cheekPuff: 0.9999924982764238,
    mouthDimpleLeft: 0.414743888682652,
    mouthRollLower: 0.32,
    mouthSmileLeft: 0.35499733688813034,
    mouthSmileRight: 0.35499733688813034,
  },
  sad: {
    mouthFrownLeft: 1,
    mouthFrownRight: 1,
    mouthShrugLower: 0.78341,
    browInnerUp: 0.452,
    eyeSquintLeft: 0.72,
    eyeSquintRight: 0.75,
    eyeLookDownLeft: 0.5,
    eyeLookDownRight: 0.5,
    jawForward: 1,
  },
  surprised: {
    eyeWideLeft: 0.5,
    eyeWideRight: 0.5,
    jawOpen: 0.351,
    mouthFunnel: 1,
    browInnerUp: 1,
  },
  angry: {
    browDownLeft: 1,
    browDownRight: 1,
    eyeSquintLeft: 1,
    eyeSquintRight: 1,
    jawForward: 1,
    jawLeft: 1,
    mouthShrugLower: 1,
    noseSneerLeft: 1,
    noseSneerRight: 0.42,
    eyeLookDownLeft: 0.16,
    eyeLookDownRight: 0.16,
    cheekSquintLeft: 1,
    cheekSquintRight: 1,
    mouthClose: 0.23,
    mouthFunnel: 0.63,
    mouthDimpleRight: 1,
  },
  crazy: {
    browInnerUp: 0.9,
    jawForward: 1,
    noseSneerLeft: 0.5700000000000001,
    noseSneerRight: 0.51,
    eyeLookDownLeft: 0.39435766259644545,
    eyeLookUpRight: 0.4039761421719682,
    eyeLookInLeft: 0.9618479575523053,
    eyeLookInRight: 0.9618479575523053,
    jawOpen: 0.9618479575523053,
    mouthDimpleLeft: 0.9618479575523053,
    mouthDimpleRight: 0.9618479575523053,
    mouthStretchLeft: 0.27893590769016857,
    mouthStretchRight: 0.2885543872656917,
    mouthSmileLeft: 0.5578718153803371,
    mouthSmileRight: 0.38473918302092225,
    tongueOut: 0.9618479575523053,
  },
};

const visemeIds: any = {
  A: "PP",
  B: "kk",
  C: "I",
  D: "AA",
  E: "O",
  F: "U",
  G: "FF",
  H: "TH",
  X: "sil",
}

const ANIMATION_FADE_TIME = 1;
let setupMode = false;

export function Avatar({interviewerAvatar}: {interviewerAvatar: string}) {
  // initialize for threejs
  const group = useRef<Group | null>(null);
  const { nodes, materials, scene } = useGLTF(
    `https://xi-docs.s3.ap-southeast-1.amazonaws.com/${interviewerAvatar.toLowerCase()}.glb`
  ) as GLTFResult;
  const { animations } = useGLTF(`https://xi-docs.s3.ap-southeast-1.amazonaws.com/${genderMapping[interviewerAvatar]}.glb`);
  const { actions } = useAnimations(animations, group);

  // initialize chatcontext
  // const { message, setPlaying, onMessagePlayed, chat } = useChat();

  // retrieve data from redux
  const dispatch = useDispatch<AppDispatch>()
  const { speechResponse } = useSelector((state: any) => state.candidateInterviewBot)
  
  // default animation
  const [animation, setAnimation] = useState<string>(
    animations.find((a) => a.name === "Idle") ? "Idle" : animations[0].name // Check if Idle animation exists otherwise use first animation
  );

  // initialize internal states
  const [lipsync, setLipsync] = useState<any[]>();
  const [blink, setBlink] = useState<boolean>(false);
  const [winkLeft, setWinkLeft] = useState<boolean>(false);
  const [winkRight, setWinkRight] = useState<boolean>(false);
  const [facialExpression, setFacialExpression] = useState<string>("");
  const [audio, setAudio] = useState<HTMLAudioElement>();

  // on message finish playing
  const messagePlayed = () => {
    // set state
    dispatch(setPlaying(false));

    // return to idle after finish talking
    setAnimation("Idle");
  }

  // --- Helper Function to Decode Base64 ---
  function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // listen for message changes when api call ends
  useEffect(() => {
    if (!speechResponse || !speechResponse.content) return;

    // setFacialExpression(message.facialExpression);
    // console.log(speechResponse.content);
    const base64Audio = "data:audio/wav;base64," + speechResponse.audio;

    // test pocketsphinx
    // (async () => {
    //   const visemeData = await window.decodeAudioAndGetPhonemesAndVisemes(base64Audio);
    //   console.log(visemeData);
    // })();

    // set intial animation and lipsync
    if (speechResponse.viseme && speechResponse.viseme.length > 0) {
      setAnimation(speechResponse.viseme[0].action);
      setLipsync(speechResponse.viseme);
    }

    const audio = new Audio(base64Audio);
    audio.play();
    audio.onended = messagePlayed;
    setAudio(audio);
  }, [speechResponse]);

  // listen for animation change
  useEffect(() => {
    try {
      // console.log(animation);
      if (actions && actions[animation]) {
        // @ts-ignore: Object is possibly 'null'.
        actions[animation]
          .reset()
          // .fadeIn(mixer.stats.actions.inUse === 0 ? 0 : 0.5)
          .fadeIn(0.5)
          .play();
  
        return () => {
          if (actions[animation]) {
            // @ts-ignore: Object is possibly 'null'.
            actions[animation].fadeOut(0.5);
          }
        }
      }
    } catch(e) {
      // ignore
    }
  }, [animation]);

  // util for morphing of threejs figure
  const lerpMorphTarget = (target: string, value: number, speed = 0.1) => {
    // console.log(target);
    scene.traverse((child) => {
      const node = child as THREE.SkinnedMesh;
      if (node.isSkinnedMesh && node.morphTargetDictionary && node.morphTargetInfluences) {
        const index = node.morphTargetDictionary[target];
        if (
          index === undefined ||
          node.morphTargetInfluences[index] === undefined
        ) {
          return;
        }

        node.morphTargetInfluences[index] = THREE.MathUtils.lerp(
          node.morphTargetInfluences[index],
          value,
          speed
        );

        if (!setupMode) {
          try {
            set({
              [target]: value,
            });
          } catch (e) { }
        }
      }
    });
  };

  // execute actions on every threejs' frame changes
  useFrame(() => {
    !setupMode &&
      nodes.EyeLeft.morphTargetDictionary &&
      Object.keys(nodes.EyeLeft.morphTargetDictionary).forEach((key) => {
        const mapping = facialExpressions[facialExpression];
        if (key === "eyeBlinkLeft" || key === "eyeBlinkRight") {
          return; // eyes wink/blink are handled separately
        }
        if (mapping && mapping[key]) {
          lerpMorphTarget(key, mapping[key], 0.1);
        } else {
          lerpMorphTarget(key, 0, 0.1);
        }
      });

    lerpMorphTarget("eyeBlinkLeft", blink || winkLeft ? 1 : 0, 0.5);
    lerpMorphTarget("eyeBlinkRight", blink || winkRight ? 1 : 0, 0.5);

    // LIPSYNC
    if (setupMode) {
      return;
    }

    if (
      speechResponse && speechResponse.content && 
      lipsync && audio && !audio.paused
    ) {
      // find lipsync using time
      const viseme = lipsync.find(v => v.time > audio.currentTime * 1000);
      if (viseme) {
        const visemeId = visemeIds[viseme.value];
        const action = viseme.action === 'Talk' ? (animation === 'Talk 1' ? 'Talk 2' : 'Talk 1') : viseme.action;

        // set lipsync
        lerpMorphTarget('viseme_' + visemeId, 1, 0.3);
        // console.log({action, animation, time: actions[animation]?.time, duration: actions[animation]?.getClip().duration});

        // console.log({ action, animation });
        // set action
        if (
          action && actions[action] &&
          actions[animation] && 
          actions[animation].time >
          actions[animation].getClip().duration - ANIMATION_FADE_TIME
        ) {
          // change of animation!
          setAnimation(animation => action);
        }
      }
    }
    // if (
    //   actions[animation].time >
    //   actions[animation].getClip().duration - ANIMATION_FADE_TIME
    // ) {
    //   setAnimation((animation) =>
    //     animation === "Talk 1" ? "Talk 2" : "Talk 1"
    //   ); // Could load more type of animations and randomization here
    // }
  });

  // setup leva controls
  useControls("FacialExpressions", {
    // chat: button(() => chat()),
    winkLeft: button(() => {
      setWinkLeft(true);
      setTimeout(() => setWinkLeft(false), 300);
    }),
    winkRight: button(() => {
      setWinkRight(true);
      setTimeout(() => setWinkRight(false), 300);
    }),
    animation: {
      value: animation,
      options: animations.map((a) => a.name),
      onChange: (value) => setAnimation(value),
    },
    facialExpression: {
      options: Object.keys(facialExpressions),
      onChange: (value) => setFacialExpression(value),
    },
    enableSetupMode: button(() => {
      setupMode = true;
    }),
    disableSetupMode: button(() => {
      setupMode = false;
    }),
    logMorphTargetValues: button(() => {
      const emotionValues: any = {};
      if (nodes.EyeLeft.morphTargetDictionary)
        Object.keys(nodes.EyeLeft.morphTargetDictionary).forEach((key) => {
          if (key === "eyeBlinkLeft" || key === "eyeBlinkRight") {
            return; // eyes wink/blink are handled separately
          }

          if (nodes.EyeLeft.morphTargetInfluences && nodes.EyeLeft.morphTargetDictionary) {

            const value =
              nodes.EyeLeft.morphTargetInfluences[
              nodes.EyeLeft.morphTargetDictionary[key]
              ];
            if (value > 0.01) {
              emotionValues[key] = value;
            }
          }
        });
      // console.log(JSON.stringify(emotionValues, null, 2));
    }),
  });

  // triggered from leva controls
  const [, set] = useControls("MorphTarget", () => {
    if (nodes.EyeLeft.morphTargetDictionary) {
      return Object.assign(
        {},
        ...Object.keys(nodes.EyeLeft.morphTargetDictionary).map((key) => {
          if (nodes.EyeLeft.morphTargetInfluences && nodes.EyeLeft.morphTargetDictionary) {
            return {
              [key]: {
                label: key,
                value: 0,
                min: nodes.EyeLeft.morphTargetInfluences[
                  nodes.EyeLeft.morphTargetDictionary[key]
                ],
                max: 1,
                onChange: (val: number) => {
                  if (setupMode) {
                    lerpMorphTarget(key, val, 1);
                  }
                },
              },
            };
          }
        })
      )
    }
  });

  // make the avatar blink on certain interval
  useEffect(() => {
    let blinkTimeout: any;
    const nextBlink = () => {
      blinkTimeout = setTimeout(() => {
        setBlink(true);
        setTimeout(() => {
          setBlink(false);
          nextBlink();
        }, 200);
      }, THREE.MathUtils.randInt(1000, 5000));
    };
    nextBlink();
    return () => clearTimeout(blinkTimeout);
  }, []);

  return (
    <group ref={group} dispose={null} position={[0, 0, -1]}>
      <primitive object={nodes.Hips} />
      <skinnedMesh geometry={nodes.Wolf3D_Hair.geometry} material={materials.Wolf3D_Hair} skeleton={nodes.Wolf3D_Hair.skeleton} />
      <skinnedMesh geometry={nodes.Wolf3D_Body.geometry} material={materials.Wolf3D_Body} skeleton={nodes.Wolf3D_Body.skeleton} />
      <skinnedMesh geometry={nodes.Wolf3D_Outfit_Bottom.geometry} material={materials.Wolf3D_Outfit_Bottom} skeleton={nodes.Wolf3D_Outfit_Bottom.skeleton} />
      <skinnedMesh geometry={nodes.Wolf3D_Outfit_Footwear.geometry} material={materials.Wolf3D_Outfit_Footwear} skeleton={nodes.Wolf3D_Outfit_Footwear.skeleton} />
      <skinnedMesh geometry={nodes.Wolf3D_Outfit_Top.geometry} material={materials.Wolf3D_Outfit_Top} skeleton={nodes.Wolf3D_Outfit_Top.skeleton} />
      <skinnedMesh name="EyeLeft" geometry={nodes.EyeLeft.geometry} material={materials.Wolf3D_Eye} skeleton={nodes.EyeLeft.skeleton} morphTargetDictionary={nodes.EyeLeft.morphTargetDictionary} morphTargetInfluences={nodes.EyeLeft.morphTargetInfluences} />
      <skinnedMesh name="EyeRight" geometry={nodes.EyeRight.geometry} material={materials.Wolf3D_Eye} skeleton={nodes.EyeRight.skeleton} morphTargetDictionary={nodes.EyeRight.morphTargetDictionary} morphTargetInfluences={nodes.EyeRight.morphTargetInfluences} />
      <skinnedMesh name="Wolf3D_Head" geometry={nodes.Wolf3D_Head.geometry} material={materials.Wolf3D_Skin} skeleton={nodes.Wolf3D_Head.skeleton} morphTargetDictionary={nodes.Wolf3D_Head.morphTargetDictionary} morphTargetInfluences={nodes.Wolf3D_Head.morphTargetInfluences} />
      <skinnedMesh name="Wolf3D_Teeth" geometry={nodes.Wolf3D_Teeth.geometry} material={materials.Wolf3D_Teeth} skeleton={nodes.Wolf3D_Teeth.skeleton} morphTargetDictionary={nodes.Wolf3D_Teeth.morphTargetDictionary} morphTargetInfluences={nodes.Wolf3D_Teeth.morphTargetInfluences} />
   </group>
  )
}

// useGLTF.preload("https://xi-docs.s3.ap-southeast-1.amazonaws.com/michael.glb");
// useGLTF.preload("https://xi-docs.s3.ap-southeast-1.amazonaws.com/lynn.glb");
useGLTF.preload("https://xi-docs.s3.ap-southeast-1.amazonaws.com/male.glb");
useGLTF.preload("https://xi-docs.s3.ap-southeast-1.amazonaws.com/female.glb");
